import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Search } from "lucide-react";
import { useState } from "react";
import { useSettingsGroups } from "~/features/settings/catalog";
import { useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/settings/")({ component: SettingsHub });

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * « Réglages » (call of 04/11): one overview over the whole width, every setting with what it is
 * for, a search across titles and descriptions; no long menu beside the pages.
 */
function SettingsHub() {
  const t = useT();
  const groups = useSettingsGroups();
  const [query, setQuery] = useState("");
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const title = (key: string) => t(`settings.pages.${key}.title` as "settings.pages.account.title");
  const summary = (key: string) => t(`settings.hub.summaries.${key}` as "settings.hub.summaries.account");
  const shown = groups
    .map((g) => ({
      ...g,
      pages: g.pages.filter((p) => {
        const text = normalize(`${t(g.label)} ${title(p.key)} ${summary(p.key)}`);
        return words.every((w) => text.includes(w));
      }),
    }))
    .filter((g) => g.pages.length > 0);

  return (
    <div className="flex flex-col gap-6">
      <label className="relative block max-w-md">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("settings.hub.search")}
          aria-label={t("settings.hub.search")}
          className="h-11 w-full rounded-control border border-line-strong bg-surface pr-3 pl-10 text-sm outline-none focus:border-gold"
        />
      </label>

      {shown.length === 0 ? (
        <p className="text-sm text-fg-3">{t("settings.hub.noResult", { query })}</p>
      ) : (
        <div className="divide-y divide-line border-y border-line">
          {shown.map((group) => (
            <section key={group.label} className="grid gap-4 py-6 lg:grid-cols-[12rem_minmax(0,1fr)] lg:gap-6">
              <h2 className="flex items-baseline gap-2 text-sm font-semibold text-fg lg:pt-3">
                {t(group.label)}
                <span className="text-xs font-normal text-fg-3 tabular">{group.pages.length}</span>
              </h2>
              <ul className="grid auto-rows-fr gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {group.pages.map((page) => (
                  <li key={page.to}>
                    <Link
                      to={page.to}
                      className="group flex h-full items-start gap-3.5 rounded-control border border-line bg-surface p-4 transition-colors hover:border-line-strong hover:bg-surface-2 focus-visible:border-gold focus-visible:outline-none"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-md border border-line bg-sunken text-fg-2 transition-colors group-hover:text-gold-text">
                        <page.icon className="size-[18px]" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-fg">{title(page.key)}</span>
                          <ChevronRight
                            className="size-4 shrink-0 text-fg-3 transition-transform group-hover:translate-x-0.5 group-hover:text-fg-2"
                            aria-hidden
                          />
                        </span>
                        <span className="mt-1 line-clamp-2 min-h-[2lh] text-[13px] leading-snug text-fg-3">
                          {summary(page.key)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
